import React, { useState } from "react";
import { Icon } from "@iconify/react";
import { toast } from "react-toastify";
import { ButtonPrimary } from "../../components/utils";
import { FileDropzone } from "../../components/shared/FileDropzone";
import { Instructions } from "../../components/shared/Instructions";
import convertToBase64 from "../../utils/convertToBase64";
import {
  useBulkExtendExerciseTemplate,
  useParseBulkExtendExerciseUpload,
} from "../../queries/esopPlan";
import { ParsedLotExtendRow } from "../../types/BulkExtendExercise";
import BulkExtendExercisePreview from "./BulkExtendExercisePreview";

const BulkExtendExercise: React.FC = () => {
  const [file, setFile] = useState<File>();
  const [errors, setErrors] = useState<string[]>([]);
  const [previewData, setPreviewData] = useState<ParsedLotExtendRow[]>([]);
  const [showPreview, setShowPreview] = useState(false);

  const { mutate: downloadTemplate, isLoading: downloadingTemplate } =
    useBulkExtendExerciseTemplate();
  const { mutate: parseUpload, isLoading: parsingUpload } =
    useParseBulkExtendExerciseUpload();

  const handleDownloadTemplate = () => {
    downloadTemplate(undefined, {
      onSuccess: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "bulk-extend-exercise-template.xlsx");
        document.body.appendChild(link);
        link.click();
        link.parentNode?.removeChild(link);
        window.URL.revokeObjectURL(url);
      },
      onError: () => {
        toast.error("Error downloading template");
      },
    });
  };

  const handleFileDrop = async (newFile: File) => {
    setFile(newFile);
    setErrors([]);
    setPreviewData([]);
  };

  const handlePreview = async () => {
    if (!file) return;
    const base64File = (await convertToBase64(file)) as string;
    parseUpload(base64File, {
      onSuccess: (result) => {
        if (
          result.errors?.length > 0 &&
          (!result.data || result.data.length === 0)
        ) {
          setErrors(result.errors);
          return;
        }
        setPreviewData(result.data);
        setErrors(result.errors?.length > 0 ? result.errors : []);
        setShowPreview(true);
      },
      onError: (error: any) => {
        const message =
          error?.response?.data?.errorRaw ||
          error?.response?.data?.errorMessage ||
          "Error parsing uploaded file";
        setErrors(Array.isArray(message) ? message : [message]);
      },
    });
  };

  if (showPreview) {
    return (
      <BulkExtendExercisePreview
        data={previewData}
        onCancel={() => {
          setShowPreview(false);
          setPreviewData([]);
          setFile(undefined);
        }}
      />
    );
  }

  return (
    <div className="w-full h-full bg-white rounded-lg shadow-sm border border-gray-200 flex flex-col min-h-[600px]">
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
        <div>
          <h2 className="text-lg font-semibold text-gray-800">
            Bulk Extend Exercise
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Upload an Excel file to extend the exercise window across many
            vesting lots at once.
          </p>
        </div>
        <ButtonPrimary
          className="flex items-center gap-2"
          onClick={handleDownloadTemplate}
          disabled={downloadingTemplate}
        >
          <Icon icon="mdi:download-outline" height={18} />
          Download Template
        </ButtonPrimary>
      </div>

      <div className="flex-1 flex flex-col items-center justify-center p-8">
        <div className="w-full max-w-2xl space-y-8">
          <div className="text-center space-y-2">
            <h3 className="text-xl font-medium text-gray-900">Upload File</h3>
            <p className="text-gray-500">
              Please ensure your file follows the template format before
              uploading.
            </p>
          </div>

          <div className="w-full max-w-2xl mx-auto">
            <FileDropzone
              onFileSelect={handleFileDrop}
              selectedFile={file}
              onClear={() => {
                setFile(undefined);
                setErrors([]);
                setPreviewData([]);
              }}
            />

            {parsingUpload && (
              <div className="mt-4 flex items-center gap-2 text-sm text-gray-500">
                <Icon icon="eos-icons:loading" className="animate-spin" />
                <span>Validating file...</span>
              </div>
            )}

            {errors.length > 0 && (
              <div className="mt-6 p-4 border border-red-200 bg-red-50 rounded-md">
                <div className="flex items-center gap-2 mb-2 text-red-700">
                  <Icon icon="material-symbols:error-outline" height={20} />
                  <span className="font-semibold">
                    {errors.length} Error{errors.length > 1 ? "s" : ""} Found
                  </span>
                </div>
                <ul className="list-disc list-inside space-y-1 text-sm text-red-600 max-h-40 overflow-y-auto pl-2">
                  {errors.map((err, index) => (
                    <li key={index}>{err}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <Instructions
            data={[
              "Use the downloaded template to ensure correct format",
              "Only vesting lots with remaining (unexercised) options are eligible for extension",
              "Refer to the 'Reference Sheet' for each lot's current vested, exercised, cashed out, lapsed, forfeited and remaining option counts",
              "New Expiry Date must be on or after the lot's vesting date",
              "You can review and edit the new expiry dates in the preview step before confirming",
            ]}
          />

          {file && (
            <div className="flex justify-center pt-4">
              <ButtonPrimary
                className="h-10 text-base shadow-md hover:shadow-lg transition-shadow"
                onClick={handlePreview}
                disabled={parsingUpload}
              >
                {parsingUpload ? (
                  <div className="flex items-center gap-2">
                    <Icon icon="eos-icons:loading" className="animate-spin" />
                    <span>Processing...</span>
                  </div>
                ) : (
                  "Preview Changes"
                )}
              </ButtonPrimary>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BulkExtendExercise;
